import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { ConfirmModal } from "../components/shared/ConfirmModal";
import { Skeleton, TableSkeletonRows } from "../components/ui/Skeleton";
import { billingApi, BillingInvoice, BillingSubscription, PlanUsage } from "../api/billingApi";

const usageUnlimitedThreshold = 1_000_000;

const plans = [
  {
    tier: "Starter",
    priceLabel: "$99",
    features: [
      "Up to 50 clients",
      "2 team seats (1 admin + 1 staff)",
      "Basic branding (logo only)",
      "Core dispute tools",
      "Email support",
    ],
  },
  {
    tier: "Growth",
    priceLabel: "$199",
    features: [
      "Up to 200 clients",
      "6 team seats",
      "White-label branding (colors, logo, portal)",
      "AI dispute reasons & automation",
      "Priority support",
    ],
  },
  {
    tier: "Agency",
    priceLabel: "$399",
    features: [
      "Unlimited clients & team seats",
      "Custom domain & full white-label",
      "Deep analytics & reporting",
      "Dedicated onboarding",
      "Custom integrations",
    ],
  },
];

function formatUsageCap(n: number): string {
  return n >= usageUnlimitedThreshold ? "Unlimited" : String(n);
}

function UsageMeterCard({
  title,
  current,
  max,
}: {
  title: string;
  current: number;
  max: number;
}) {
  const unlimited = max >= usageUnlimitedThreshold;
  const ratio = unlimited || max <= 0 ? 0 : current / max;
  const pct = Math.min(100, ratio * 100);
  const barClass =
    unlimited || pct < 70 ? "bg-emerald-500" : pct < 90 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
      <p className="text-sm font-medium text-slate-400">{title}</p>
      <p className="mt-1 text-lg font-semibold text-white">
        {current} / {formatUsageCap(max)}
        {!unlimited && max > 0 ? (
          <span className="text-sm font-normal text-slate-500"> ({Math.round(pct)}%)</span>
        ) : null}
      </p>
      {!unlimited && max > 0 ? (
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-800">
          <div
            className={`h-full rounded-full transition-all ${barClass}`}
            style={{ width: `${pct}%` }}
            aria-hidden
          />
        </div>
      ) : null}
      {!unlimited && pct >= 90 ? (
        <p className="mt-2 text-xs text-amber-200">
          You&apos;re near your plan limit. Upgrade on Billing for more capacity.
        </p>
      ) : null}
    </div>
  );
}

function WhiteLabelUsageCard({ enabled }: { enabled: boolean }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-4">
      <p className="text-sm font-medium text-slate-400">White-label</p>
      <p className={`mt-1 text-lg font-semibold ${enabled ? "text-emerald-400" : "text-slate-500"}`}>
        {enabled ? "Active" : "Locked"}
      </p>
      {!enabled ? (
        <p className="mt-2 text-xs text-slate-500">Growth and Agency include full portal branding.</p>
      ) : null}
    </div>
  );
}

function formatMoney(cents: number | null | undefined, currency: string | null | undefined) {
  if (cents == null) {
    return "—";
  }
  const cur = (currency || "usd").toUpperCase();
  return new Intl.NumberFormat(undefined, { style: "currency", currency: cur }).format(cents / 100);
}

function normalizeTierName(t: string | null | undefined): string {
  if (!t) {
    return "";
  }
  const x = t.trim();
  if (/^starter$/i.test(x)) {
    return "Starter";
  }
  if (/^growth$/i.test(x)) {
    return "Growth";
  }
  if (/^agency$/i.test(x)) {
    return "Agency";
  }
  return x;
}

const TIER_RANK: Record<string, number> = { Starter: 1, Growth: 2, Agency: 3 };

type PlanButtonVariant = "primary" | "current" | "upgrade" | "downgrade";

type ConfirmPlanDialogState = {
  show: true;
  planName: string;
  action: "upgrade" | "downgrade" | "subscribe";
};

function getPlanButtonState(
  planName: string,
  currentPlan: string | null,
  currentStatus: string | null
): { label: string; disabled: boolean; variant: PlanButtonVariant } {
  const isActive =
    currentStatus === "active" ||
    currentStatus === "trialing" ||
    currentStatus === "past_due";

  if (!isActive || !currentPlan) {
    return { label: `Subscribe to ${planName}`, disabled: false, variant: "primary" };
  }

  const currentNorm = normalizeTierName(currentPlan);
  const targetNorm = normalizeTierName(planName);
  const currentRank = TIER_RANK[currentNorm] ?? 0;
  const targetRank = TIER_RANK[targetNorm] ?? 0;

  if (currentRank === targetRank) {
    return { label: "Current plan", disabled: true, variant: "current" };
  }
  if (targetRank > currentRank) {
    return { label: `Upgrade to ${planName}`, disabled: false, variant: "upgrade" };
  }
  return { label: `Downgrade to ${planName}`, disabled: false, variant: "downgrade" };
}

function isBillableForPlanActions(sub: BillingSubscription | null): boolean {
  if (!sub) {
    return false;
  }
  const s = sub.status;
  return s === "active" || s === "trialing" || s === "past_due";
}

function SubscriptionStatusDisplay({ status }: { status: string }) {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "trialing") {
    return <p className="font-medium text-emerald-400">Active</p>;
  }
  if (s === "past_due") {
    return <p className="font-medium text-amber-400">Past due</p>;
  }
  if (s === "canceled" || s === "cancelled") {
    return <p className="font-medium text-red-400">Canceled</p>;
  }
  return <p className="text-slate-300">No active subscription</p>;
}

function hasNoManagedSubscription(sub: BillingSubscription | null | undefined): boolean {
  return Boolean(sub && sub.status === "none");
}

const BillingPage: React.FC = () => {
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [planActionLoading, setPlanActionLoading] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSubConfirmOpen, setCancelSubConfirmOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmPlanDialogState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const [sub, inv] = await Promise.all([billingApi.getSubscription(), billingApi.getInvoices()]);
      setSubscription(sub);
      setInvoices(inv);
      try {
        const usage = await billingApi.getUsage();
        setPlanUsage(usage);
      } catch {
        setPlanUsage(null);
      }
    } catch {
      setError("Could not load billing data. Check Stripe configuration and your connection.");
      setSubscription(null);
      setInvoices([]);
      setPlanUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      void load();
      return;
    }

    void (async () => {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      try {
        const sub = await billingApi.verifyCheckout(sessionId);
        setSubscription(sub);
        const inv = await billingApi.getInvoices();
        setInvoices(inv);
        try {
          setPlanUsage(await billingApi.getUsage());
        } catch {
          setPlanUsage(null);
        }
        setSuccessMessage("Subscription activated successfully!");
      } catch {
        console.error("Failed to verify checkout");
        setError("Could not confirm checkout with Stripe. Showing saved subscription if any.");
        await load();
      } finally {
        setLoading(false);
        const path = window.location.pathname || "/billing";
        window.history.replaceState({}, "", path);
      }
    })();
  }, [load]);

  useEffect(() => {
    if (!confirmDialog?.show) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setConfirmDialog(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmDialog?.show]);

  const handlePlanChange = async (planName: string) => {
    const status = subscription?.status ?? "";
    const isBillable =
      status === "active" || status === "trialing" || status === "past_due";

    setPlanActionLoading(planName);
    setError("");
    setSuccessMessage("");
    try {
      if (isBillable) {
        const updated = await billingApi.changePlan(planName);
        setSubscription(updated);
        try {
          setPlanUsage(await billingApi.getUsage());
        } catch {
          setPlanUsage(null);
        }
        setSuccessMessage(`Plan changed to ${planName}!`);
      } else {
        const { url } = await billingApi.createCheckoutSession(planName);
        if (url) {
          window.location.href = url;
        }
      }
    } catch {
      setError(
        isBillable
          ? "Failed to change plan. Please try again."
          : "Could not start checkout. Ensure Stripe:SecretKey is set on the API."
      );
    } finally {
      setPlanActionLoading(null);
    }
  };

  const handlePlanClick = (planName: string) => {
    if (!isBillableForPlanActions(subscription)) {
      void handlePlanChange(planName);
      return;
    }

    const currentNorm = normalizeTierName(subscription?.tier);
    const targetNorm = normalizeTierName(planName);
    const currentRank = TIER_RANK[currentNorm] ?? 0;
    const targetRank = TIER_RANK[targetNorm] ?? 0;
    const action: "upgrade" | "downgrade" = targetRank > currentRank ? "upgrade" : "downgrade";

    setConfirmDialog({ show: true, planName, action });
  };

  const confirmPlanChange = () => {
    if (!confirmDialog) {
      return;
    }
    const { planName } = confirmDialog;
    setConfirmDialog(null);
    void handlePlanChange(planName);
  };

  const performCancelSubscription = async () => {
    setCancelSubConfirmOpen(false);
    setCancelLoading(true);
    setError("");
    try {
      await billingApi.cancelSubscription();
      await load();
    } catch {
      setError("Cancellation failed. You may not have an active Stripe subscription.");
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {confirmDialog?.show && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="presentation"
          onClick={() => setConfirmDialog(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="billing-plan-confirm-title"
            className="w-full max-w-lg rounded-lg border border-slate-600 bg-slate-800 p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="billing-plan-confirm-title" className="mb-2 text-lg font-semibold text-white">
              {confirmDialog.action === "upgrade" ? "Confirm upgrade" : "Confirm downgrade"}
            </h3>

            {confirmDialog.action === "upgrade" ? (
              <div className="mb-6 space-y-2 text-sm text-slate-300">
                <p>
                  You&apos;re upgrading from{" "}
                  <span className="font-medium text-emerald-400">{normalizeTierName(subscription?.tier) || subscription?.tier}</span>{" "}
                  to <span className="font-medium text-emerald-400">{confirmDialog.planName}</span>.
                </p>
                <p>
                  You&apos;ll be charged the prorated difference for the remainder of this billing cycle. Your next full
                  charge will reflect the new plan price.
                </p>
              </div>
            ) : (
              <div className="mb-6 space-y-2 text-sm text-slate-300">
                <p>
                  You&apos;re downgrading from{" "}
                  <span className="font-medium text-amber-400">{normalizeTierName(subscription?.tier) || subscription?.tier}</span>{" "}
                  to <span className="font-medium text-amber-400">{confirmDialog.planName}</span>.
                </p>
                <p>
                  You&apos;ll receive a prorated credit for the unused time on your current plan. This credit will be
                  applied to future invoices.
                </p>
              </div>
            )}

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={() => setConfirmDialog(null)}
                className="w-full flex-1 rounded-md border border-slate-500 py-2.5 font-medium text-slate-300 transition hover:bg-slate-700 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmPlanChange()}
                className={`w-full flex-1 rounded-md py-2.5 font-medium text-white transition sm:w-auto ${
                  confirmDialog.action === "upgrade"
                    ? "bg-emerald-500 hover:bg-emerald-600"
                    : "bg-amber-500 hover:bg-amber-600"
                }`}
              >
                {confirmDialog.action === "upgrade" ? "Yes, upgrade" : "Yes, downgrade"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white">Billing</h1>
            <p className="text-sm text-slate-400">Subscription, upgrades, and invoice history (Stripe).</p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
          >
            Refresh
          </button>
        </div>

        {successMessage && (
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-100">
            {successMessage}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">{error}</div>
        )}

        {!loading && hasNoManagedSubscription(subscription) ? (
          <div className="rounded-xl border border-cyan-500/35 bg-cyan-950/25 px-4 py-4 text-slate-100">
            <p className="text-lg font-semibold text-white">Welcome! Pick a plan to get started.</p>
            <p className="mt-2 text-sm text-cyan-100/90">
              Choose a plan below to start your 14-day free trial with Stripe. You won&apos;t be charged until the trial
              ends, and you can cancel anytime.
            </p>
          </div>
        ) : null}

        {loading || (subscription && subscription.status !== "none") ? (
          <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-lg font-semibold text-white">Current subscription</h2>
            {loading ? (
              <div className="mt-4 space-y-3" aria-busy="true" aria-label="Loading subscription">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-full max-w-md" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ) : subscription ? (
              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm text-slate-400">Plan tier</p>
                  <p className="text-xl font-semibold text-emerald-300">{subscription.tier}</p>
                  <p className="mt-2 text-sm text-slate-400">Status</p>
                  <SubscriptionStatusDisplay status={subscription.status} />
                  {subscription.amountCents != null && (
                    <p className="mt-2 text-sm text-slate-400">
                      Amount{" "}
                      <span className="text-slate-200">
                        {formatMoney(subscription.amountCents, subscription.currency)}
                      </span>{" "}
                      / mo
                    </p>
                  )}
                  {subscription.currentPeriodEnd &&
                    (subscription.status === "active" || subscription.status === "trialing") && (
                      <div className="mt-2">
                        <p className="text-sm text-slate-400">Next billing date</p>
                        <p className="text-white">{new Date(subscription.currentPeriodEnd).toLocaleDateString()}</p>
                      </div>
                    )}
                  {subscription.currentPeriodEnd &&
                    subscription.status !== "none" &&
                    subscription.status !== "active" &&
                    subscription.status !== "trialing" && (
                      <p className="mt-2 text-xs text-slate-500">
                        Current period ends{" "}
                        {new Date(subscription.currentPeriodEnd).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </p>
                    )}
                </div>
                {subscription.subscriptionId &&
                  subscription.status !== "none" &&
                  subscription.status !== "canceled" &&
                  subscription.status !== "cancelled" && (
                  <button
                    type="button"
                    onClick={() => setCancelSubConfirmOpen(true)}
                    disabled={cancelLoading}
                    className="rounded-lg border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-200 hover:bg-red-950/40 disabled:opacity-50"
                  >
                    {cancelLoading ? "Cancelling…" : "Cancel subscription"}
                  </button>
                )}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-400">No data.</p>
            )}
          </section>
        ) : null}

        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Current usage</h2>
          <p className="mt-1 text-sm text-slate-400">Clients and team seats versus your plan limits.</p>
          {loading ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Skeleton className="h-28 w-full rounded-xl" />
              <Skeleton className="h-28 w-full rounded-xl" />
              <Skeleton className="h-28 w-full rounded-xl" />
            </div>
          ) : !planUsage ? (
            <p className="mt-3 text-sm text-slate-500">Usage stats could not be loaded. Try Refresh.</p>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <UsageMeterCard title="Clients" current={planUsage.currentClients} max={planUsage.maxClients} />
              <UsageMeterCard title="Team seats" current={planUsage.currentTeamSeats} max={planUsage.maxTeamSeats} />
              <WhiteLabelUsageCard enabled={planUsage.whiteLabelEnabled} />
            </div>
          )}
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {plans.map((plan) => {
            const subStatus = subscription?.status ?? "";
            const hasManagedSub =
              !!subscription &&
              (subStatus === "active" || subStatus === "trialing" || subStatus === "past_due");
            const currentTierForButtons = hasManagedSub
              ? normalizeTierName(subscription!.tier) || null
              : null;
            const btnState = getPlanButtonState(plan.tier, currentTierForButtons, subscription?.status ?? null);
            const showCurrentBadge =
              currentTierForButtons === normalizeTierName(plan.tier) && hasManagedSub;

            return (
              <div
                key={plan.tier}
                className="rounded-xl border border-slate-800 bg-slate-900 p-5"
              >
                {showCurrentBadge ? (
                  <div className="mb-3 flex justify-start">
                    <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-xs font-medium text-emerald-400">
                      Current
                    </span>
                  </div>
                ) : null}
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm text-emerald-300">{plan.tier}</p>
                  <p className="text-lg font-bold text-white">
                    {plan.priceLabel}
                    <span className="text-sm font-normal text-slate-400">/mo</span>
                  </p>
                </div>
                <ul className="mt-3 space-y-2 text-sm text-slate-300">
                  {plan.features.map((feature) => (
                    <li key={feature}>— {feature}</li>
                  ))}
                </ul>
                {btnState.variant === "current" ? (
                  <button
                    type="button"
                    disabled
                    className="mt-4 w-full cursor-not-allowed rounded-md bg-slate-600 py-3 text-sm font-medium text-slate-400"
                  >
                    {btnState.label}
                  </button>
                ) : btnState.variant === "downgrade" ? (
                  <button
                    type="button"
                    onClick={() => handlePlanClick(plan.tier)}
                    disabled={planActionLoading !== null}
                    className="mt-4 w-full rounded-md border border-slate-500 py-3 text-sm font-medium text-slate-300 transition hover:bg-slate-700 disabled:opacity-50"
                  >
                    {planActionLoading === plan.tier
                      ? isBillableForPlanActions(subscription)
                        ? "Updating…"
                        : "Opening Stripe…"
                      : btnState.label}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      btnState.variant === "upgrade" ? handlePlanClick(plan.tier) : void handlePlanChange(plan.tier)
                    }
                    disabled={planActionLoading !== null}
                    className="mt-4 w-full rounded-md bg-emerald-500 py-3 text-sm font-medium text-white transition hover:bg-emerald-600 disabled:opacity-50"
                  >
                    {planActionLoading === plan.tier
                      ? isBillableForPlanActions(subscription)
                        ? "Updating…"
                        : "Opening Stripe…"
                      : btnState.label}
                  </button>
                )}
              </div>
            );
          })}
        </section>

        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="mb-4 text-lg font-semibold text-white">Invoice history</h2>
          {loading ? (
            <TableSkeletonRows rows={5} cols={5} />
          ) : invoices.length === 0 ? (
            <p className="text-sm text-slate-500">No invoices yet. After you subscribe, paid invoices will appear here.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400">
                    <th className="pb-3">Invoice</th>
                    <th className="pb-3">Date</th>
                    <th className="pb-3">Amount</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-800/60 text-slate-200">
                      <td className="py-3 font-mono text-xs">{invoice.number || invoice.id}</td>
                      <td className="py-3">
                        {invoice.created ? new Date(invoice.created).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-3 text-emerald-300">
                        {formatMoney(invoice.amountPaidCents, invoice.currency)}
                      </td>
                      <td className="py-3 capitalize">{invoice.status}</td>
                      <td className="py-3">
                        {invoice.hostedInvoiceUrl || invoice.pdfUrl ? (
                          <a
                            href={invoice.hostedInvoiceUrl || invoice.pdfUrl || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-400 hover:underline"
                          >
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ConfirmModal
        isOpen={cancelSubConfirmOpen}
        title="Cancel subscription"
        message="Cancel your IntelliWarrior subscription? You will keep access until the end of the billing period where applicable."
        confirmLabel="Cancel subscription"
        confirmVariant="warning"
        onConfirm={() => void performCancelSubscription()}
        onCancel={() => setCancelSubConfirmOpen(false)}
      />
    </DashboardLayout>
  );
};

export default BillingPage;
